import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getNumberOfMarkets } from "../lib/api";
import { getDateFormat, isObjEmpty } from "../lib";
import { convertOdds } from "../lib/odds";

export const useMarketSelections = ({
  isMultipleSelection,
  marketType = "",
}) => {
  const dispatch = useDispatch();
  const { experiments } = useSelector((state) => state.view);
  const { bubbleUI } = experiments;
  const { prefetchedFilters } = useSelector((state) => state.filter);
  const [emptyFilters, setEmptyFilters] = useState(false);
  const [marketSelectionsCount, setMarketSelectionsCount] = useState({
    fetching: true,
    count: 0,
    prevCount: Number(localStorage.getItem("selectionCount")) ?? 0,
  });

  const getNumberOfMarketSelections = async (params) => {
    const res = await getNumberOfMarkets(params);
    if (res.code === 0) {
      setMarketSelectionsCount((prevState) => ({
        fetching: false,
        count: res.count,
        prevCount: prevState.count,
      }));
    } else {
      setMarketSelectionsCount({
        fetching: false,
        count: 0,
        prevCount: 0,
      });
    }
  };

  const buildApiParams = () => {
    const areFiltersEmpty =
      (prefetchedFilters.date.length === 0 ||
        prefetchedFilters.date.length === 2) &&
      !prefetchedFilters.fixtures.today.length &&
      !prefetchedFilters.fixtures.tomorrow.length &&
      !prefetchedFilters.odds.minOdds &&
      !prefetchedFilters.odds.maxOdds &&
      !prefetchedFilters.markets.length;

    setEmptyFilters(areFiltersEmpty);

    if (areFiltersEmpty) {
      setMarketSelectionsCount((prevState) => ({
        ...prevState,
        fetching: true,
        prevCount: 0,
      }));
      return null;
    }

    const {
      minOdds,
      maxOdds,
      // isDecimal flag is a new flag used to determine if the odds are in decimal format or
      // using the current implementation where the odds are stored as the position in the odds slider
      isDecimal = false,
    } = !isObjEmpty(prefetchedFilters.odds)
      ? prefetchedFilters.odds
      : { minOdds: null, maxOdds: null };

    const convertedMinOdds =
      (isDecimal || bubbleUI) && minOdds ? minOdds.toFixed(2) : minOdds;
    const convertedMaxOdds =
      (isDecimal || bubbleUI) && maxOdds ? maxOdds.toFixed(2) : maxOdds;

    const odds = {
      minOdds: convertOdds({ odds: convertedMinOdds, type: "min" }),
      maxOdds: convertOdds({ odds: convertedMaxOdds, type: "max" }),
    };

    return {
      basedOn: prefetchedFilters.stats,
      date:
        prefetchedFilters.date.length === 1
          ? getDateFormat(prefetchedFilters.date[0])
          : null,
      fixtures:
        [
          ...prefetchedFilters.fixtures.today,
          ...prefetchedFilters.fixtures.tomorrow,
        ].join() || null,
      markets: prefetchedFilters.markets.join() || null,
      minOdds: odds.minOdds?.toString() || null,
      maxOdds: odds.maxOdds?.toString() || null,
    };
  };

  const renderNumberOfMarketSelections = () => {
    if (
      emptyFilters ||
      (marketSelectionsCount.fetching && marketSelectionsCount.prevCount === 0)
    ) {
      return "Show all selections";
    }

    if (
      marketSelectionsCount.fetching &&
      marketSelectionsCount.count === 0 &&
      marketSelectionsCount.prevCount > 0
    ) {
      return `Show ${marketSelectionsCount.prevCount} selections`;
    }

    if (marketSelectionsCount.count > 999) {
      return `Show ${999}+ selections`;
    }

    return `Show ${marketSelectionsCount.count} selections`;
  };

  useEffect(() => {
    const params = buildApiParams();

    if (isMultipleSelection && params) {
      getNumberOfMarketSelections(params);
    }

    if (marketType && params) {
      getNumberOfMarketSelections({
        ...params,
        basedOn: marketType,
      });
    }

    return () => {
      dispatch({ type: "UpdateFavouritesResponse", payload: null });
    };
  }, [prefetchedFilters, isMultipleSelection]);

  return {
    emptyFilters,
    marketSelectionsCount,
    renderNumberOfMarketSelections,
  };
};
